class StratumConfigurator {
  constructor() {
    this.data = JSON.parse(document.getElementById('stratum-configurator-data').textContent);

    this.productTitle = this.data.productTitle;
    this.options = this.data.options;
    this.variants = this.data.variants;
    this.configLayers = this.data.configLayers;
    this.staticLayers = this.data.staticLayers;
    this.extraComponents = this.data.extraComponents;

    this.selectedOptions = new Map(); // position (1-indexed) -> value
    this.selectedVariant = null;
    this.selectedComponent = null; // currently selected config layer id
    this.extrasCart = []; // { handle, variantId, title, variantTitle, price, quantity, available }
    this.kitIncluded = true; // whether keyboard kit is in the configuration
    this.currentView = 'above'; // 'above' | 'underside'
    this.showPlaceholders = this.data.showPlaceholders !== false;

    this.currencySymbol = this.data.currencySymbol || '$';
    this.basePrice = Math.min(...this.variants.map(v => v.price));
    this.preorderStatus = this.data.preorderStatus || 'live';
    this.isPreorderProduct = !!this.data.isPreorderProduct;
    this.isExtrasPreorder = !!this.data.extrasPreorder;
    this.preorderShowAgreement = !!this.data.preorderShowAgreement;
    this.preorderAgreementText = this.data.preorderAgreementText;

    this.loadingOverlay = document.getElementById('loading-overlay');
    this.preloadedImages = new Set();
    this.toastTimeouts = new Map();
    this.orangeBlueprintCache = new Map(); // original src -> orange data URL
    this._viewTimeout = null;

    // Build a list of configurable layer IDs for navigation
    this.configurableIds = this.configLayers.map(l => l.layerId);

    // Build position map: optionName -> position
    this.optionPositionMap = new Map();
    this.options.forEach(opt => {
      this.optionPositionMap.set(opt.name, opt.position);
    });

    // Mobile state
    this.isMobile = window.innerWidth <= 768;
    this.carouselIndex = 0;
    this.carouselCards = [];

    this.init();
  }

  init() {
    this.setupComponentCards();
    this.setupVisibilityToggles();
    this.setupKeyboardNavigation();
    this.initializeViewToggle();
    this.initializeConfiguratorSummary();
    this.initializeSpecsAccordion();
    this.setupExtrasUI();
    this.setupAddToCart();
    this.preloadAllImages();
    this.updateDisplayPrice();

    // Set up preorder checkbox
    const preorderCheckbox = document.querySelector('#cb');
    if (preorderCheckbox) {
      preorderCheckbox.addEventListener('change', () => this.updateAddToCartButton());
    }

    // Navigation buttons
    const prevBtn = document.querySelector('.prev-component');
    const nextBtn = document.querySelector('.forward-component');
    if (prevBtn) prevBtn.addEventListener('click', () => this.navigateComponents('prev'));
    if (nextBtn) nextBtn.addEventListener('click', () => this.navigateComponents('next'));

    // Select first configurable component
    if (this.configurableIds.length > 0) {
      this.selectComponent(this.configurableIds[0]);
    }

    // Fade out loading overlay
    if (this.loadingOverlay) {
      setTimeout(() => this.loadingOverlay.classList.add('fade-out'), 800);
    }

    // Mobile layout
    if (this.isMobile) this.initMobileLayout();
    window.addEventListener('resize', this.handleResize.bind(this));

    // Check cart for view-cart link
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const link = document.querySelector('.view-cart-link');
        if (link && cart.item_count > 0) link.style.display = 'block';
      })
      .catch(() => {});
  }

  //#region Utilities
  handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  formatPrice(cents) {
    return `${this.currencySymbol}${(cents / 100).toFixed(2)}`;
  }

  showToast(message, type = 'info') {
    const container = document.querySelector('.toast-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    container.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('visible'));

    const timeout = setTimeout(() => {
      toast.classList.remove('visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);

    this.toastTimeouts.set(toast, timeout);
  }

  // Map view state (above/underside) to image data keys (front/back)
  get viewImageKey() {
    return this.currentView === 'underside' ? 'back' : 'front';
  }

  // Current keyboard price — based on all current selections, even partial
  get currentPrice() {
    if (this.selectedVariant) return this.selectedVariant.price;
    if (this.selectedOptions.size === 0) return this.basePrice;

    // Find min price among variants matching all current selections
    const matching = this.variants.filter(v => {
      for (const [pos, val] of this.selectedOptions) {
        if (v[`option${pos}`] !== val) return false;
      }
      return true;
    });
    return matching.length > 0 ? Math.min(...matching.map(v => v.price)) : this.basePrice;
  }

  updateDisplayPrice() {
    const el = document.getElementById('configurator-display-price');
    if (!el) return;
    el.textContent = this.formatPrice(this.currentPrice);
  }
  //#endregion

  //#region Component Cards
  setupComponentCards() {
    document.querySelectorAll('.component-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (card.classList.contains('locked')) return;
        // Don't trigger card click from visibility toggle
        if (e.target.closest('.visibility-toggle')) return;
        const layerId = card.dataset.componentId;
        this.selectComponent(layerId);
      });
    });
  }

  selectComponent(layerId) {
    const layer = this.configLayers.find(l => l.layerId === layerId);
    if (!layer) return;

    this.selectedComponent = layerId;

    // Update card selection styling
    document.querySelectorAll('.component-card').forEach(c => c.classList.remove('selected'));
    const card = document.querySelector(`.component-card[data-component-id="${layerId}"]`);
    if (card) {
      card.classList.add('selected');
      if (!this.isMobile) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    // Auto-switch view to component's preferred view
    const viewChanging = layer.preferredView && layer.preferredView !== this.currentView;
    if (viewChanging) {
      this.setView(layer.preferredView);
    }

    if (this.isMobile) {
      // On mobile, just update preview + pulse
      if (!viewChanging) this.updateBlueprintPulse();
      return;
    }

    // Desktop-only updates
    const titleEl = document.querySelector('.selected-component-title');
    if (titleEl) titleEl.textContent = layer.displayName;

    const subtitleEl = document.querySelector('.selected-component-subtitle');
    if (subtitleEl) subtitleEl.textContent = layer.displayName;

    const navImage = document.querySelector('.component-navigation__image');
    if (navImage) {
      const position = this.optionPositionMap.get(layer.optionName);
      const selectedValue = this.selectedOptions.get(position);
      if (selectedValue && layer.images[selectedValue]) {
        navImage.src = layer.images[selectedValue][this.viewImageKey] || layer.blueprintFront || '';
      } else {
        navImage.src = layer.blueprintFront || '';
      }
    }

    this.updateNavigationButtons();
    this.populateOptionsGrid(layer);
    if (!viewChanging) {
      this.updateBlueprintPulse();
    }
  }

  navigateComponents(direction) {
    const currentIndex = this.configurableIds.indexOf(this.selectedComponent);
    if (currentIndex === -1) return;

    let nextIndex;
    if (direction === 'prev') {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) nextIndex = this.configurableIds.length - 1;
    } else {
      nextIndex = currentIndex + 1;
      if (nextIndex >= this.configurableIds.length) nextIndex = 0;
    }

    this.selectComponent(this.configurableIds[nextIndex]);
  }

  updateNavigationButtons() {
    const currentIndex = this.configurableIds.indexOf(this.selectedComponent);
    const prevBtn = document.querySelector('.prev-component');
    const nextBtn = document.querySelector('.forward-component');
    if (prevBtn) prevBtn.style.visibility = this.configurableIds.length > 1 ? 'visible' : 'hidden';
    if (nextBtn) nextBtn.style.visibility = this.configurableIds.length > 1 ? 'visible' : 'hidden';
  }
  //#endregion

  //#region Options Grid & Variant Resolution
  populateOptionsGrid(layer) {
    const grid = document.querySelector('.options-grid');
    if (!grid) return;

    grid.innerHTML = '';

    const position = this.optionPositionMap.get(layer.optionName);
    if (!position) return;

    const option = this.options.find(o => o.position === position);
    if (!option) return;

    const container = document.createElement('div');
    container.className = 'option-container';

    const label = document.createElement('label');
    label.textContent = option.name;

    const select = document.createElement('select');
    select.className = 'option-select';
    select.dataset.position = position;
    select.dataset.optionName = this.handleize(option.name);

    // Default option
    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = `Select ${option.name}`;
    defaultOpt.disabled = true;
    defaultOpt.selected = !this.selectedOptions.has(position);
    select.appendChild(defaultOpt);

    // Populate values with availability info
    const availableValues = this.getAvailableValuesForPosition(position);
    availableValues.forEach(({ value, labelText }) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = labelText;
      if (this.selectedOptions.get(position) === value) {
        opt.selected = true;
      }
      select.appendChild(opt);
    });

    select.addEventListener('change', (e) => this.handleOptionChange(e, layer));

    container.appendChild(label);
    container.appendChild(select);
    grid.appendChild(container);

    // Show the grid (base CSS hides it by default)
    grid.classList.add('active');

    // Update apply button state
    this.updateApplyButton();
  }

  getAvailableValuesForPosition(position) {
    const results = [];
    const option = this.options.find(o => o.position === position);
    if (!option) return results;

    const referencePrice = this.currentPrice;
    const currentSelection = this.selectedOptions.get(position);

    // Check if changing this option actually affects price
    const allPricesForOption = this.variants.map(v => v.price);
    const uniquePrices = new Set();
    option.values.forEach(val => {
      const matching = this.variants.filter(v => {
        if (v[`option${position}`] !== val) return false;
        for (const [pos, sel] of this.selectedOptions) {
          if (pos === position) continue;
          if (v[`option${pos}`] !== sel) return false;
        }
        return true;
      });
      matching.forEach(v => uniquePrices.add(v.price));
    });
    const optionAffectsPrice = uniquePrices.size > 1;

    option.values.forEach(value => {
      // Find all variants matching this value at this position + any prior selections
      const matching = this.variants.filter(v => {
        const optionKey = `option${position}`;
        if (v[optionKey] !== value) return false;

        // Check prior selections
        for (const [pos, sel] of this.selectedOptions) {
          if (pos === position) continue;
          if (v[`option${pos}`] !== sel) return false;
        }
        return true;
      });

      if (matching.length === 0) return; // skip values with no compatible variants

      const prices = matching.map(v => v.price);
      const minPrice = Math.min(...prices);
      const allSoldOut = matching.every(v => !v.available);

      let labelText = value;
      // Show delta only if: this option affects price, and this isn't the already-selected value
      if (optionAffectsPrice && value !== currentSelection) {
        const delta = minPrice - referencePrice;
        if (delta > 0) {
          labelText += ` (+${this.formatPrice(delta)})`;
        } else if (delta < 0) {
          labelText += ` (-${this.formatPrice(Math.abs(delta))})`;
        }
      }
      if (allSoldOut) labelText += ' - SOLD OUT';

      results.push({ value, labelText });
    });

    return results;
  }

  handleOptionChange(event, layer) {
    const select = event.target;
    const position = parseInt(select.dataset.position, 10);
    const value = select.value;

    if (!value) return;

    this.selectedOptions.set(position, value);

    // On mobile, auto-swap to preferred view when selecting a variant
    if (this.isMobile && layer.preferredView && layer.preferredView !== this.currentView) {
      this.setView(layer.preferredView);
    }

    // Update layer image
    this.updateConfigLayerImage(layer.layerId, value);

    // Update component card
    this.updateComponentCard(layer.layerId, value);

    // Update navigation image
    const navImage = document.querySelector('.component-navigation__image');
    if (navImage && layer.images[value]) {
      navImage.src = layer.images[value][this.viewImageKey] || layer.blueprintFront || '';
    }

    // Clear pulse on the configured layer
    this.updateBlueprintPulse();

    // Update price display (works with partial selections)
    this.updateDisplayPrice();

    // Resolve variant
    this.resolveVariant();

    // Re-render the dropdown so deltas recalculate relative to new price
    this.refreshCurrentDropdown(layer, position, value);

    // On mobile, refresh all carousel dropdowns so price deltas update
    if (this.isMobile) this.refreshAllMobileDropdowns();

    // Update apply button
    this.updateApplyButton();
  }

  resolveVariant() {
    // Need all options selected
    if (this.selectedOptions.size < this.options.length) {
      this.selectedVariant = null;
      this.updateConfigSummary();
      this.updateAddToCartButton();
      return;
    }

    // Find matching variant
    this.selectedVariant = this.variants.find(v => {
      for (const [pos, val] of this.selectedOptions) {
        if (v[`option${pos}`] !== val) return false;
      }
      return true;
    }) || null;

    this.updateConfigSummary();
    this.updateAddToCartButton();
    this.updateDisplayPrice();
  }

  refreshCurrentDropdown(layer, position, selectedValue) {
    const select = document.querySelector(`.option-select[data-position="${position}"]`);
    if (!select) return;

    const availableValues = this.getAvailableValuesForPosition(position);

    // Update option text without rebuilding the whole select (preserves selection)
    Array.from(select.options).forEach(opt => {
      if (!opt.value) return; // skip placeholder
      const match = availableValues.find(v => v.value === opt.value);
      if (match) opt.textContent = match.labelText;
    });
  }

  updateApplyButton() {
    const btn = document.querySelector('.apply-selection-button');
    if (!btn) return;

    const layer = this.configLayers.find(l => l.layerId === this.selectedComponent);
    if (!layer) return;

    const position = this.optionPositionMap.get(layer.optionName);
    const hasSelection = this.selectedOptions.has(position);

    btn.disabled = !hasSelection;
    btn.textContent = hasSelection ? 'Applied' : 'Add to Configuration';

    if (hasSelection) {
      btn.disabled = true;
    }
  }
  //#endregion

  //#region Blueprint Pulse
  updateBlueprintPulse() {
    // Remove all existing pulse overlays
    document.querySelectorAll('.blueprint-pulse-overlay').forEach(el => el.remove());

    // Add pulse to the selected component's layer if no option is chosen
    if (!this.selectedComponent) return;
    const layer = this.configLayers.find(l => l.layerId === this.selectedComponent);
    if (!layer) return;

    const position = this.optionPositionMap.get(layer.optionName);
    const hasSelection = this.selectedOptions.has(position);
    if (hasSelection) return;

    const layerEl = document.getElementById(`layer-${layer.layerId}`);
    if (layerEl && !layerEl.classList.contains('hidden')) {
      layerEl.style.opacity = '';
      layerEl.style.transition = '';
      this.addPulseOverlay(layerEl);
    }
  }

  addPulseOverlay(imgEl) {
    const originalSrc = imgEl.src;

    const createOverlay = (orangeSrc) => {
      const overlay = document.createElement('img');
      overlay.src = orangeSrc;
      overlay.className = 'component-layer blueprint-pulse-overlay';
      overlay.style.zIndex = parseInt(imgEl.style.zIndex || 0) + 1;
      overlay.style.pointerEvents = 'none';
      imgEl.parentElement.appendChild(overlay);
    };

    // Check cache first
    if (this.orangeBlueprintCache.has(originalSrc)) {
      createOverlay(this.orangeBlueprintCache.get(originalSrc));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
        if (data[i + 3] > 10 && avg < 230) {
          data[i] = 232;
          data[i + 1] = 101;
          data[i + 2] = 45;
          data[i + 3] = 255;
        } else {
          data[i + 3] = 0;
        }
      }

      ctx.putImageData(imageData, 0, 0);
      const orangeSrc = canvas.toDataURL('image/png');
      this.orangeBlueprintCache.set(originalSrc, orangeSrc);
      createOverlay(orangeSrc);
    };
    img.onerror = () => {};
    img.src = originalSrc;
  }
  //#endregion

  //#region Layer Image Updates
  updateConfigLayerImage(layerId, optionValue) {
    const layer = this.configLayers.find(l => l.layerId === layerId);
    if (!layer) return;

    const layerEl = document.getElementById(`layer-${layerId}`);
    if (!layerEl) return;

    const imageData = layer.images[optionValue];
    if (imageData) {
      const src = this.currentView === 'underside' ? imageData.back : imageData.front;
      if (src) {
        layerEl.src = src;
        layerEl.classList.remove('hidden');
        layerEl.classList.add('visible');
      }
    }
  }

  updateComponentCard(layerId, optionValue) {
    const card = document.querySelector(`.component-card[data-component-id="${layerId}"]`);
    if (!card) return;

    const layer = this.configLayers.find(l => l.layerId === layerId);
    if (!layer) return;

    // Update card image to show the selected variant render
    const imageData = layer.images[optionValue];
    if (imageData) {
      const src = imageData.front || layer.blueprintFront;
      const cardImage = card.querySelector('.component-card__image');
      if (cardImage && src) cardImage.src = src;
    }

    // Show status badge (checkmark)
    const badge = card.querySelector('.status-badge');
    if (badge) badge.classList.remove('hidden');

    // Update option flag text
    const flag = card.querySelector('.option-flag');
    if (flag) {
      flag.textContent = optionValue;
      flag.classList.add('selected');
    }

    card.classList.add('configured');
  }

  updateAllLayersForView() {
    const totalLayers = this.configLayers.length + this.staticLayers.length;

    // Update config layers
    this.configLayers.forEach(layer => {
      const layerEl = document.getElementById(`layer-${layer.layerId}`);
      if (!layerEl) return;

      const position = this.optionPositionMap.get(layer.optionName);
      const selectedValue = this.selectedOptions.get(position);

      if (selectedValue && layer.images[selectedValue]) {
        const src = this.currentView === 'underside'
          ? layer.images[selectedValue].back
          : layer.images[selectedValue].front;
        if (src) layerEl.src = src;
      } else {
        // Show blueprint for current view
        const blueprint = this.currentView === 'underside' ? layer.blueprintBack : layer.blueprintFront;
        if (blueprint) layerEl.src = blueprint;
      }

      // Reverse z-index for back view
      if (this.currentView === 'underside') {
        layerEl.style.zIndex = totalLayers + 1 - layer.layerIndex;
      } else {
        layerEl.style.zIndex = layer.layerIndex;
      }
    });

    // Update static layers
    this.staticLayers.forEach(layer => {
      const layerEl = document.getElementById(`layer-${layer.layerId}`);
      if (!layerEl) return;

      if (this.currentView === 'underside') {
        layerEl.src = layerEl.dataset.backImage || layerEl.dataset.frontImage;
        layerEl.style.zIndex = totalLayers + 1 - layer.layerIndex;
      } else {
        layerEl.src = layerEl.dataset.frontImage;
        layerEl.style.zIndex = layer.layerIndex;
      }
    });
  }
  //#endregion

  //#region View Toggle
  initializeViewToggle() {
    const pill = document.querySelector('.view-toggle-pill');
    if (!pill) { console.warn('[ViewToggle] No .view-toggle-pill found'); return; }

    console.log('[ViewToggle] Initializing, found pill:', pill);

    pill.querySelectorAll('.view-toggle-option').forEach(option => {
      console.log('[ViewToggle] Attaching listener to option:', option.dataset.option, option);

      // Use pointerdown as backup — catches touch before click on iOS
      option.addEventListener('pointerdown', (e) => {
        console.log('[ViewToggle] pointerdown on:', option.dataset.option, 'currentView:', this.currentView);
      });

      option.addEventListener('click', (e) => {
        const newView = option.dataset.option;
        console.log('[ViewToggle] click fired on:', newView, 'currentView:', this.currentView, 'event:', e);
        console.log('[ViewToggle] button element:', option, 'parentElement:', option.parentElement);
        console.log('[ViewToggle] computed pointer-events:', getComputedStyle(option).pointerEvents);
        console.log('[ViewToggle] computed z-index (pill):', getComputedStyle(pill).zIndex);

        if (newView !== this.currentView) {
          console.log('[ViewToggle] Calling setView(' + newView + ')');
          this.setView(newView);
        } else {
          console.log('[ViewToggle] Already on this view, skipping');
        }
      });
    });

    // Debug: listen on the pill container too
    pill.addEventListener('click', (e) => {
      console.log('[ViewToggle] Pill container click, target:', e.target, 'target.dataset.option:', e.target.dataset?.option);
    });
  }

  setView(view) {
    console.log('[setView] Called with:', view, 'previous:', this.currentView);
    console.trace('[setView] Call stack');
    this.currentView = view;

    const pill = document.querySelector('.view-toggle-pill');
    if (pill) {
      pill.setAttribute('data-view', view);
      pill.querySelectorAll('.view-toggle-option').forEach(opt => {
        opt.classList.toggle('active', opt.dataset.option === view);
      });
    }

    const container = document.querySelector('.preview-image-container');
    if (container) {
      container.classList.toggle('underside-view', view === 'underside');

      // Remove pulse overlays immediately before fading
      container.querySelectorAll('.blueprint-pulse-overlay').forEach(el => el.remove());

      const layers = container.querySelectorAll('.component-layer');

      // Fade out only the layers (logo stays visible)
      layers.forEach(el => {
        el.style.transition = 'opacity 0.2s ease';
        el.style.opacity = '0';
      });

      // Cancel any in-flight view transition to prevent race conditions
      if (this._viewTimeout) clearTimeout(this._viewTimeout);

      // After fully faded, swap src then wait for all images to load before fading in
      this._viewTimeout = setTimeout(() => {
        this.updateAllLayersForView();

        // Wait for every layer image to finish loading
        const loadPromises = [...layers].map(el => {
          if (el.complete) return Promise.resolve();
          return new Promise(resolve => {
            el.onload = resolve;
            el.onerror = resolve;
          });
        });

        Promise.all(loadPromises).then(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              layers.forEach(el => {
                el.style.opacity = '1';
              });
              // After fade-in completes, clean up inline styles
              setTimeout(() => {
                layers.forEach(el => {
                  el.style.transition = '';
                  el.style.opacity = '';
                });
                this.updateBlueprintPulse();
              }, 250);
            });
          });
        });
      }, 250);
    } else {
      this.updateAllLayersForView();
    }

    // Update navigation image if a component is selected
    if (this.selectedComponent) {
      const layer = this.configLayers.find(l => l.layerId === this.selectedComponent);
      if (layer) {
        const navImage = document.querySelector('.component-navigation__image');
        const position = this.optionPositionMap.get(layer.optionName);
        const selectedValue = this.selectedOptions.get(position);
        if (navImage && selectedValue && layer.images[selectedValue]) {
          const imgKey = view === 'underside' ? 'back' : 'front';
          navImage.src = layer.images[selectedValue][imgKey] || layer.blueprintFront || '';
        }
      }
    }
  }
  //#endregion

  //#region Visibility Toggles
  setupVisibilityToggles() {
    document.querySelectorAll('.visibility-toggle').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const card = toggle.closest('.component-card');
        if (!card) return;

        const layerId = card.dataset.componentId;
        const layerImage = document.getElementById(`layer-${layerId}`);
        const eyeVisible = toggle.querySelector('.eye-visible');
        const eyeHidden = toggle.querySelector('.eye-hidden');

        if (layerImage && eyeVisible && eyeHidden) {
          const isVisible = !layerImage.classList.contains('hidden');
          if (isVisible) {
            layerImage.classList.add('hidden');
            eyeVisible.classList.add('hidden');
            eyeHidden.classList.remove('hidden');
          } else {
            layerImage.classList.remove('hidden');
            eyeVisible.classList.remove('hidden');
            eyeHidden.classList.add('hidden');
          }
        }
      });
    });
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (event) => {
      // Skip if modal is open
      const modal = document.querySelector('.image-modal');
      if (modal && modal.classList.contains('active')) return;

      // Skip if extras picker is open
      const extrasPicker = document.querySelector('.extras-picker-overlay');
      if (extrasPicker && !extrasPicker.classList.contains('hidden')) return;

      // Tab from last dropdown → focus apply button
      if (event.target.tagName === 'SELECT' && event.key === 'Tab' && !event.shiftKey) {
        const select = event.target;
        if (select.value && select.value !== '') {
          event.preventDefault();
          const applyButton = document.querySelector('.apply-selection-button');
          if (applyButton) {
            applyButton.focus();
          }
        }
        return;
      }

      // Arrow left/right to navigate components
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Don't hijack arrow keys when focused on a select/input
        if (event.target.tagName === 'SELECT' || event.target.tagName === 'INPUT') return;

        event.preventDefault();
        this.navigateComponents(event.key === 'ArrowLeft' ? 'prev' : 'next');
      }
    });
  }
  //#endregion

  //#region Configuration Summary
  initializeConfiguratorSummary() {
    const anchor = document.querySelector('.configuration-summary-anchor');
    if (!anchor) return;

    const summaryContainer = document.createElement('div');
    summaryContainer.className = 'configuration-summary';

    // Header
    const header = document.createElement('button');
    header.className = 'summary-header';
    header.setAttribute('aria-expanded', 'true');
    header.innerHTML = `
      <h2>Configuration Breakdown</h2>
      <svg class="chevron-icon" viewBox="0 0 24 24" width="24" height="24">
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
      </svg>
    `;

    // Content
    const content = document.createElement('div');
    content.className = 'summary-content';
    content.innerHTML = `
      <div class="summary-section summary-kit-section">
        <div class="summary-section__header">
          <div>
            <p class="artifact-subtitle">Keyboard Configuration</p>
            <h3 class="summary-section__title">Keyboard Configuration</h3>
          </div>
          <button type="button" class="kit-toggle-button" title="Remove keyboard kit">✕</button>
        </div>
        <div class="summary-items"></div>
        <div class="summary-kit-disclaimer" style="display:none">
          <p>No keyboard kit options have been configured. You are only purchasing add-ons.</p>
        </div>
        <div class="summary-keyboard-price">
          <span class="summary-keyboard-price__label">Keyboard Price</span>
          <span class="summary-keyboard-price__amount"></span>
        </div>
      </div>
      <div class="summary-addons-only-notice" style="display:none">
        <p>You are purchasing <strong>add-ons only</strong> — no keyboard kit is included in this order.</p>
        <button type="button" class="kit-restore-button">+ Add Keyboard Kit</button>
      </div>
      <div class="stratum-extras-section">
        <p class="artifact-subtitle">Add-Ons</p>
        <h3 class="summary-section__title summary-extras-title">Add-Ons</h3>
        <div class="extras-list"></div>
        <button type="button" class="extras-add-button">+ Add-Ons</button>
      </div>
    `;

    // Extras preorder sells kits only — no add-ons section
    if (this.isExtrasPreorder) {
      content.querySelector('.stratum-extras-section').style.display = 'none';
    }

    // Footer
    const footer = document.createElement('div');
    footer.className = 'summary-footer';
    footer.innerHTML = `
      <div class="summary-total">
        <div class="summary-total__title">Total</div>
        <div class="summary-total__price">${this.currencySymbol}0.00</div>
      </div>
      <div class="cart-actions">
        <button type="button" class="btn add-to-cart-button product-form__cart-submit disabled" disabled>
          ${this.isPreorderClosed() ? this.preorderClosedLabel() : (this.isExtrasPreorder ? 'Configure Your Kit' : 'Configure Your Kit or Add-Ons')}
        </button>
        <a href="/cart" class="view-cart-link" style="display:none">View Cart &rarr;</a>
      </div>
    `;

    summaryContainer.appendChild(header);
    summaryContainer.appendChild(content);
    summaryContainer.appendChild(footer);

    // Move preorder agreement into the footer, between total and add-to-cart
    const preorderAgreement = document.querySelector('.preorder-agreement');
    if (preorderAgreement) {
      const cartActions = footer.querySelector('.cart-actions');
      footer.insertBefore(preorderAgreement, cartActions);
    }

    anchor.appendChild(summaryContainer);

    // Toggle
    header.addEventListener('click', () => {
      const expanded = header.getAttribute('aria-expanded') === 'true';
      header.setAttribute('aria-expanded', !expanded);
      content.style.display = expanded ? 'none' : 'block';
    });

    // Kit remove / restore buttons
    content.querySelector('.kit-toggle-button').addEventListener('click', () => {
      this.kitIncluded = false;
      this.updateKitVisibility();
      this.updateConfigSummary();
      this.updateAddToCartButton();
    });
    content.querySelector('.kit-restore-button').addEventListener('click', () => {
      this.kitIncluded = true;
      this.updateKitVisibility();
      this.updateConfigSummary();
      this.updateAddToCartButton();
    });

    // Show default state immediately
    this.updateConfigSummary();
  }

  updateConfigSummary() {
    const summaryItems = document.querySelector('.summary-items');
    if (!summaryItems) return;

    let html = '';

    this.configLayers.forEach(layer => {
      const position = this.optionPositionMap.get(layer.optionName);
      const value = this.selectedOptions.get(position);

      // Calculate price delta for this option's selected value
      let deltaHtml = '';
      if (value) {
        const delta = this.getOptionPriceDelta(position, value);
        if (delta > 0) {
          deltaHtml = `<span class="summary-item__delta">+${this.formatPrice(delta)}</span>`;
        } else if (delta < 0) {
          deltaHtml = `<span class="summary-item__delta">-${this.formatPrice(Math.abs(delta))}</span>`;
        }
      }

      html += `
        <div class="summary-item${value ? '' : ' summary-item--empty'}">
          <p class="artifact-subtitle summary-item__subtitle">${layer.displayName}</p>
          <div class="summary-item__row">
            <div>
              <div class="summary-item__label">${layer.displayName}</div>
              <div class="summary-item__value">${value || 'Not selected'}</div>
            </div>
            ${deltaHtml}
          </div>
        </div>
      `;
    });

    summaryItems.innerHTML = html;

    // Show/hide kit disclaimer and remove button based on state
    const hasExtras = this.extrasCart.length > 0;
    const hasAnyKitOption = this.selectedOptions.size > 0;
    const kitComplete = this.selectedOptions.size >= this.options.length && !!this.selectedVariant;
    const disclaimer = document.querySelector('.summary-kit-disclaimer');
    const disclaimerText = disclaimer?.querySelector('p');
    const removeBtn = document.querySelector('.kit-toggle-button');
    if (disclaimer && disclaimerText) {
      if (!kitComplete && hasExtras && this.kitIncluded) {
        disclaimer.style.display = '';
        if (hasAnyKitOption) {
          disclaimerText.textContent = 'Keyboard kit will not be added to your order until all options are selected. Currently only add-ons will be purchased.';
        } else {
          disclaimerText.textContent = 'No keyboard kit options have been configured. You are only purchasing add-ons.';
        }
      } else {
        disclaimer.style.display = 'none';
      }
    }
    if (removeBtn) removeBtn.style.display = (hasAnyKitOption && this.kitIncluded && !this.isExtrasPreorder) ? '' : 'none';

    // Update extras list
    this.updateExtrasListDisplay();
    this.updateTotalPrice();
  }

  updateKitVisibility() {
    const kitSection = document.querySelector('.summary-kit-section');
    const notice = document.querySelector('.summary-addons-only-notice');
    if (kitSection) kitSection.style.display = this.kitIncluded ? '' : 'none';
    if (notice) notice.style.display = this.kitIncluded ? 'none' : '';
  }

  getOptionPriceDelta(position, value) {
    // Find min price with this value selected (plus other current selections)
    const withValue = this.variants.filter(v => {
      if (v[`option${position}`] !== value) return false;
      for (const [pos, sel] of this.selectedOptions) {
        if (pos === position) continue;
        if (v[`option${pos}`] !== sel) return false;
      }
      return true;
    });

    // Find min price with cheapest value for this position (plus other current selections)
    const withCheapest = this.variants.filter(v => {
      for (const [pos, sel] of this.selectedOptions) {
        if (pos === position) continue;
        if (v[`option${pos}`] !== sel) return false;
      }
      return true;
    });

    if (withValue.length === 0 || withCheapest.length === 0) return 0;

    const selectedMinPrice = Math.min(...withValue.map(v => v.price));
    const cheapestMinPrice = Math.min(...withCheapest.map(v => v.price));
    return selectedMinPrice - cheapestMinPrice;
  }

  updateExtrasListDisplay() {
    const extrasList = document.querySelector('.extras-list');
    if (!extrasList) return;

    if (this.extrasCart.length === 0) {
      extrasList.innerHTML = '';
      return;
    }

    let html = '';
    this.extrasCart.forEach((extra, index) => {
      const lineTotal = extra.price * extra.quantity;
      html += `
        <div class="extras-line-item" data-index="${index}">
          <div class="extras-line-item__info">
            <p class="artifact-subtitle extras-line-item__subtitle">${extra.title}</p>
            <div class="extras-line-item__name">${extra.title}</div>
            <div class="extras-line-item__variant">${extra.variantTitle}</div>
          </div>
          <div class="extras-line-item__controls">
            <input type="number" class="extras-qty-input" value="${extra.quantity}" min="1" max="99" data-index="${index}">
            <span class="extras-line-item__price">${this.formatPrice(lineTotal)}</span>
            <button type="button" class="extras-remove-item" data-index="${index}" aria-label="Remove">
              <svg width="16" height="16" viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/></svg>
            </button>
          </div>
        </div>
      `;
    });

    extrasList.innerHTML = html;

    // Attach qty change and remove handlers
    extrasList.querySelectorAll('.extras-qty-input').forEach(input => {
      input.addEventListener('change', (e) => {
        const idx = parseInt(e.target.dataset.index);
        const qty = Math.max(1, parseInt(e.target.value) || 1);
        this.extrasCart[idx].quantity = qty;
        e.target.value = qty;
        this.updateConfigSummary();
      });
    });

    extrasList.querySelectorAll('.extras-remove-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt(e.currentTarget.dataset.index);
        this.extrasCart.splice(idx, 1);
        this.updateConfigSummary();
      });
    });

    // Track overflow for gradient fade
    this.updateExtrasOverflow(extrasList);
    extrasList.addEventListener('scroll', () => this.updateExtrasOverflow(extrasList));
  }

  updateExtrasOverflow(el) {
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    el.classList.toggle('has-overflow', !hasOverflow || atBottom);
  }

  updateTotalPrice() {
    const kitComplete = this.kitIncluded && this.selectedVariant?.available;
    const keyboardPrice = kitComplete ? this.currentPrice : 0;
    let total = keyboardPrice;
    this.extrasCart.forEach(extra => {
      total += extra.price * extra.quantity;
    });

    // Keyboard price line
    const kbPriceEl = document.querySelector('.summary-keyboard-price__amount');
    if (kbPriceEl) kbPriceEl.textContent = this.formatPrice(keyboardPrice);

    const totalEl = document.querySelector('.summary-total__price');
    if (totalEl) totalEl.textContent = this.formatPrice(total);

    const summary = document.querySelector('.configuration-summary');
    if (summary) summary.classList.toggle('has-items', total > 0);
  }
  //#endregion

  //#region Specs Accordion
  initializeSpecsAccordion() {
    const specsSection = document.querySelector('.configurator-specs');
    if (!specsSection) return;

    const specsContent = specsSection.innerHTML;
    specsSection.innerHTML = '';

    const header = document.createElement('button');
    header.className = 'summary-header';
    header.setAttribute('aria-expanded', 'false');
    header.innerHTML = `
      <h2>Specs</h2>
      <svg class="chevron-icon" viewBox="0 0 24 24" width="24" height="24">
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
      </svg>
    `;

    const content = document.createElement('div');
    content.className = 'specs-content';
    content.style.display = 'none';
    content.innerHTML = specsContent.replace(/<h2>Specs<\/h2>/, '').trim();

    specsSection.appendChild(header);
    specsSection.appendChild(content);

    header.addEventListener('click', () => {
      const expanded = header.getAttribute('aria-expanded') === 'true';
      header.setAttribute('aria-expanded', !expanded);
      content.style.display = expanded ? 'none' : 'block';
    });
  }
  //#endregion

  //#region Add to Cart Button
  isPreorderClosed() {
    return this.preorderStatus !== 'live' && this.preorderStatus !== 'complete' && !this.isExtrasPreorder;
  }

  preorderClosedLabel() {
    return `Preorder Ended — Status: ${String(this.preorderStatus).toUpperCase()}`;
  }

  updateAddToCartButton() {
    const btn = document.querySelector('.add-to-cart-button');
    if (!btn) return;

    // Preorder closed — nothing is purchasable, whatever the stock says
    if (this.isPreorderClosed()) {
      btn.textContent = this.preorderClosedLabel();
      btn.disabled = true;
      btn.classList.add('disabled');
      return;
    }

    const hasExtras = this.extrasCart.length > 0;
    const allSelected = this.selectedOptions.size >= this.options.length;
    const hasVariant = !!this.selectedVariant;
    const variantAvailable = this.selectedVariant?.available;
    const kitComplete = allSelected && hasVariant && variantAvailable;
    // Kit is effectively excluded if user removed it OR if kit options are incomplete
    const kitEffectivelyExcluded = !this.kitIncluded || !kitComplete;

    // Check preorder agreement (on mobile, modal handles this — skip checkbox check)
    let agreementOk = true;
    if (this.preorderShowAgreement && !this.isMobile) {
      const checkbox = document.querySelector('#cb');
      agreementOk = checkbox?.checked ?? false;
    }

    if (kitEffectivelyExcluded && hasExtras && !agreementOk) {
      btn.textContent = 'Please Accept Agreement';
      btn.disabled = true;
      btn.classList.add('disabled');
    } else if (kitEffectivelyExcluded && hasExtras) {
      // Has addons, kit not fully configured — allow addons-only purchase
      btn.textContent = 'Add Add-Ons to Cart';
      btn.disabled = false;
      btn.classList.remove('disabled');
    } else if (kitEffectivelyExcluded && !hasExtras) {
      // Nothing to purchase
      const remaining = this.options.length - this.selectedOptions.size;
      if (!this.kitIncluded) {
        btn.textContent = 'Select Add-Ons or Add Keyboard Kit';
      } else {
        btn.textContent = `${remaining} Option${remaining > 1 ? 's' : ''} Remaining`;
      }
      btn.disabled = true;
      btn.classList.add('disabled');
    } else if (!variantAvailable) {
      btn.textContent = 'Sold Out';
      btn.disabled = true;
      btn.classList.add('disabled');
    } else if (!agreementOk) {
      btn.textContent = 'Please Accept Agreement';
      btn.disabled = true;
      btn.classList.add('disabled');
    } else {
      btn.textContent = this.isExtrasPreorder ? 'Add Extras Preorder to Cart' : 'Add to Cart';
      btn.disabled = false;
      btn.classList.remove('disabled');
    }
  }

  setupAddToCart() {
    const btn = document.querySelector('.add-to-cart-button');
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.disabled) return;

      btn.classList.add('loading');
      btn.textContent = 'Adding to Cart...';
      btn.disabled = true;

      try {
        await this.addConfigurationToCart();
        btn.classList.remove('loading');
        btn.textContent = 'Added to Cart!';

        const viewCartLink = document.querySelector('.view-cart-link');
        if (viewCartLink) viewCartLink.style.display = 'block';
      } catch (error) {
        btn.classList.remove('loading');
        btn.textContent = this.isExtrasPreorder ? 'Add Extras Preorder to Cart' : 'Add to Cart';
        btn.disabled = false;
        console.error('Error adding to cart:', error);
        this.showToast('Error adding to cart. Please try again.', 'error');
      }
    });
  }

  getVisibleLayerImages() {
    const allLayers = [...this.configLayers, ...this.staticLayers]
      .sort((a, b) => a.layerIndex - b.layerIndex);

    return allLayers
      .map(layer => {
        const el = document.getElementById(`layer-${layer.layerId}`);
        return (el && !el.classList.contains('hidden')) ? el.src : null;
      })
      .filter(Boolean);
  }

  async addConfigurationToCart() {
    if (this.preorderStatus !== 'live' && this.preorderStatus !== 'complete' && !this.isExtrasPreorder) return;

    const items = [];

    // Main keyboard variant (skip if kit removed)
    if (this.kitIncluded && this.selectedVariant?.available) {
      const properties = {
        '_Bundle Type': 'stratum_config',
        '_Parent Product': this.productTitle,
        '_Layer Images': this.getVisibleLayerImages().join('|')
      };

      // Add option properties
      this.selectedOptions.forEach((value, position) => {
        const option = this.options.find(o => o.position === position);
        if (option) {
          properties[`_option_${option.name.toLowerCase().replace(/\s+/g, '_')}`] = value;
        }
      });

      items.push({
        id: this.selectedVariant.id,
        quantity: 1,
        properties
      });
    }

    // Extras — independent line items
    this.extrasCart.forEach(extra => {
      items.push({
        id: extra.variantId,
        quantity: extra.quantity
      });
    });

    if (items.length === 0) {
      this.showToast('No items to add to cart', 'error');
      return;
    }

    // Batch items in groups of 5 to avoid Shopify request size limits
    const batchSize = 5;
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: batch })
      });

      // Handle empty or non-JSON responses
      const text = await response.text();
      if (!response.ok) {
        let message = 'Failed to add items to cart';
        try {
          const data = JSON.parse(text);
          message = data.description || message;
        } catch (e) { /* response wasn't JSON */ }
        throw new Error(message);
      }
    }

    if (typeof refreshCart === 'function') {
      refreshCart();
    } else {
      window.location.href = '/cart';
    }
  }
  //#endregion

  //#region Extras UI
  setupExtrasUI() {
    const addBtn = document.querySelector('.extras-add-button');
    if (!addBtn) return;

    addBtn.addEventListener('click', () => this.openExtrasPicker());

    // Close modal
    const overlay = document.querySelector('.extras-picker-overlay');
    const closeBtn = document.querySelector('.extras-picker-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeExtrasPicker());
    if (overlay) overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeExtrasPicker();
    });

    // Confirm button
    const confirmBtn = document.querySelector('.extras-confirm-button');
    if (confirmBtn) confirmBtn.addEventListener('click', () => this.confirmExtrasSelection());

    // Event delegation for extras list (qty changes and removes)
    const extrasList = document.querySelector('.extras-list');
    if (extrasList) {
      extrasList.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.extras-remove-item');
        if (removeBtn) {
          const variantId = parseInt(removeBtn.dataset.variantId, 10);
          this.removeExtra(variantId);
        }
      });
      extrasList.addEventListener('input', (e) => {
        if (e.target.classList.contains('extras-qty-input')) {
          const variantId = parseInt(e.target.dataset.variantId, 10);
          const newQty = Math.max(1, parseInt(e.target.value, 10) || 1);
          this.updateExtraQuantity(variantId, newQty);
        }
      });
    }
  }

  openExtrasPicker() {
    const overlay = document.querySelector('.extras-picker-overlay');
    if (overlay) overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    this.buildExtrasPickerList();
  }

  closeExtrasPicker() {
    const overlay = document.querySelector('.extras-picker-overlay');
    if (overlay) overlay.classList.add('hidden');
    document.body.style.overflow = '';
  }

  isSimpleProduct(comp) {
    return comp.variants.length <= 1;
  }

  buildExtrasPickerList() {
    const listContainer = document.querySelector('.extras-picker-list');
    if (!listContainer) return;

    listContainer.innerHTML = this.extraComponents.map(comp => {
      const isSimple = this.isSimpleProduct(comp);
      const variant = comp.variants[0];
      const price = variant ? this.formatPrice(variant.price) : '';

      if (isSimple) {
        const inCart = variant ? this.extrasCart.find(e => e.variantId === variant.id) : null;
        const checked = inCart ? 'checked' : '';
        const qty = inCart ? inCart.quantity : 1;
        return `
          <div class="extras-picker-row" data-handle="${comp.handle}">
            <div class="extras-picker-row__main">
              <label class="extras-picker-row__label">
                <input type="checkbox" class="extras-picker-checkbox" data-handle="${comp.handle}" ${variant ? `data-variant-id="${variant.id}"` : ''} ${checked}>
                <div class="extras-picker-row__text">
                  <span class="artifact-subtitle extras-picker-row__subtitle">${comp.title}</span>
                  <span class="extras-picker-row__title">${comp.title}</span>
                </div>
              </label>
              <div class="extras-picker-row__controls">
                <span class="extras-picker-row__price">${price}</span>
                <input type="number" class="extras-picker-qty" value="${qty}" min="1" data-handle="${comp.handle}" ${variant ? `data-variant-id="${variant.id}"` : ''}>
              </div>
            </div>
          </div>
        `;
      } else {
        // Multi-variant: accordion header + expandable variant rows
        return `
          <div class="extras-picker-row extras-picker-row--accordion expanded" data-handle="${comp.handle}">
            <button class="extras-picker-row__header" type="button" data-handle="${comp.handle}">
              <div class="extras-picker-row__text">
                <span class="artifact-subtitle extras-picker-row__subtitle">${comp.title}</span>
                <span class="extras-picker-row__title">${comp.title}</span>
              </div>
              <div class="extras-picker-row__meta">
                <span class="extras-picker-row__count">${comp.variants.length} variants</span>
                <svg class="extras-picker-row__chevron" width="16" height="16" viewBox="0 0 24 24">
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor"/>
                </svg>
              </div>
            </button>
            <div class="extras-picker-row__variants">
              ${comp.variants.map(v => {
                const inCart = this.extrasCart.find(e => e.variantId === v.id);
                const checked = inCart ? 'checked' : '';
                const qty = inCart ? inCart.quantity : 1;
                return `
                <div class="extras-picker-variant" data-variant-id="${v.id}">
                  <label class="extras-picker-variant__label">
                    <input type="checkbox" class="extras-picker-checkbox" data-handle="${comp.handle}" data-variant-id="${v.id}" ${checked}>
                    <span class="extras-picker-variant__title">${v.title}</span>
                  </label>
                  <div class="extras-picker-variant__controls">
                    <span class="extras-picker-variant__price">${this.formatPrice(v.price)}</span>
                    <input type="number" class="extras-picker-qty" value="${qty}" min="1" data-handle="${comp.handle}" data-variant-id="${v.id}">
                  </div>
                </div>
              `}).join('')}
            </div>
          </div>
        `;
      }
    }).join('');

    // Accordion toggle for multi-variant items
    listContainer.querySelectorAll('.extras-picker-row__header').forEach(header => {
      header.addEventListener('click', () => {
        const row = header.closest('.extras-picker-row');
        const variants = row.querySelector('.extras-picker-row__variants');
        const isOpen = !variants.classList.contains('hidden');
        variants.classList.toggle('hidden', isOpen);
        row.classList.toggle('expanded', !isOpen);
      });
    });
  }

  confirmExtrasSelection() {
    const listContainer = document.querySelector('.extras-picker-list');
    if (!listContainer) return;

    // Build new extras cart from current checkbox state
    const newCart = [];
    listContainer.querySelectorAll('.extras-picker-checkbox').forEach(cb => {
      if (!cb.checked) return;

      const handle = cb.dataset.handle;
      const variantId = parseInt(cb.dataset.variantId, 10);
      const comp = this.extraComponents.find(c => c.handle === handle);
      if (!comp) return;

      const variant = comp.variants.find(v => v.id === variantId);
      if (!variant) return;

      const qtyInput = listContainer.querySelector(`.extras-picker-qty[data-variant-id="${variantId}"]`);
      const qty = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

      newCart.push({
        handle: comp.handle,
        variantId: variant.id,
        title: comp.title,
        variantTitle: variant.title,
        price: variant.price,
        quantity: qty
      });
    });

    const addedCount = newCart.length;
    this.extrasCart = newCart;

    if (addedCount > 0) {
      this.showToast(`${addedCount} extra${addedCount > 1 ? 's' : ''} added`, 'success');
      this.updateConfigSummary();
      this.updateAddToCartButton();
    }

    this.closeExtrasPicker();
  }

  removeExtra(variantId) {
    this.extrasCart = this.extrasCart.filter(e => e.variantId !== variantId);
    this.updateConfigSummary();
    this.updateAddToCartButton();
    this.showToast('Extra component removed', 'info');
  }
  //#endregion

  //#region Image Preloading
  preloadAllImages() {
    const imagesToPreload = [];

    this.configLayers.forEach(layer => {
      if (layer.blueprintFront) imagesToPreload.push(layer.blueprintFront);
      if (layer.blueprintBack) imagesToPreload.push(layer.blueprintBack);
      Object.values(layer.images).forEach(img => {
        if (img.front) imagesToPreload.push(img.front);
        if (img.back) imagesToPreload.push(img.back);
      });
    });

    this.staticLayers.forEach(layer => {
      if (layer.frontImage) imagesToPreload.push(layer.frontImage);
      if (layer.backImage) imagesToPreload.push(layer.backImage);
    });

    const unique = [...new Set(imagesToPreload)];
    const preloadChunk = (start, size) => {
      const end = Math.min(start + size, unique.length);
      for (let i = start; i < end; i++) {
        if (!this.preloadedImages.has(unique[i])) {
          const img = new Image();
          img.onload = () => {
            this.preloadedImages.add(unique[i]);
            if (i === end - 1 && end < unique.length) {
              setTimeout(() => preloadChunk(end, size), 100);
            }
          };
          img.src = unique[i];
        }
      }
    };
    preloadChunk(0, 5);
  }
  //#endregion

  //#region Mobile Carousel

  initMobileLayout() {
    this.isMobile = true;

    const preview = document.querySelector('.configurator__preview');
    if (!preview) return;

    // 1. Relocate title + price above the preview area
    const titleBlock = document.querySelector('.configurator-description');
    if (titleBlock) {
      let titleWrapper = document.querySelector('.mobile-title-price');
      if (!titleWrapper) {
        titleWrapper = document.createElement('div');
        titleWrapper.className = 'mobile-title-price';
      }
      titleWrapper.appendChild(titleBlock);
      preview.parentNode.insertBefore(titleWrapper, preview);
    }

    // 2. Move view toggle BELOW the preview image container (not inside it, to avoid overflow:hidden clipping)
    const viewToggle = document.querySelector('.view-toggle-container');
    if (viewToggle) {
      const mobileToggleWrap = document.createElement('div');
      mobileToggleWrap.className = 'mobile-view-toggle';
      mobileToggleWrap.appendChild(viewToggle);
      const previewImageContainer = preview.querySelector('.preview-image-container');
      if (previewImageContainer) {
        previewImageContainer.insertAdjacentElement('afterend', mobileToggleWrap);
        console.log('[MobileLayout] View toggle placed AFTER preview-image-container (outside overflow:hidden)');
      } else {
        preview.appendChild(mobileToggleWrap);
      }
    }

    // 3. Move components header out of preview, then insert carousel after preview
    //    (Keycult pattern: carousel is a sibling AFTER .configurator__preview)
    const headerContainer = document.querySelector('.components-header-container');
    if (headerContainer) {
      preview.insertAdjacentElement('afterend', headerContainer);
    }

    let carouselContainer = document.querySelector('.mobile-carousel-container');
    if (!carouselContainer) {
      carouselContainer = document.createElement('div');
      carouselContainer.className = 'mobile-carousel-container';
    }
    const track = document.createElement('div');
    track.className = 'mobile-carousel-track';
    carouselContainer.appendChild(track);

    // Insert carousel after the header (which is now after preview)
    if (headerContainer) {
      headerContainer.insertAdjacentElement('afterend', carouselContainer);
    } else {
      preview.insertAdjacentElement('afterend', carouselContainer);
    }

    // 3. Build carousel cards for ALL components
    this.carouselCards = [];

    // Configurable layers
    this.configLayers.forEach(layer => {
      const card = this.buildMobileCarouselCard(layer, true);
      this.carouselCards.push(card);
      track.appendChild(card);
    });

    // Static layers
    this.staticLayers.forEach(layer => {
      const card = this.buildMobileCarouselCard(layer, false);
      this.carouselCards.push(card);
      track.appendChild(card);
    });

    // 4. Attach swipe handlers
    this.attachGestureHandlers(carouselContainer);

    // 5. Render carousel
    this.carouselIndex = 0;
    this.renderMobileCarousel();

    // 6. Create bottom bar and move summary into it
    let bottomBar = document.querySelector('.mobile-bottom-bar');
    if (!bottomBar) {
      bottomBar = document.createElement('div');
      bottomBar.className = 'mobile-bottom-bar';
      document.body.appendChild(bottomBar);
    }

    const summary = document.querySelector('.configuration-summary');
    if (summary) {
      bottomBar.appendChild(summary);

      // Collapse summary by default on mobile
      const header = summary.querySelector('.summary-header');
      const content = summary.querySelector('.summary-content');
      if (header && content) {
        header.setAttribute('aria-expanded', 'false');
        content.style.display = 'none';
      }
    }

    // 7. Set up preorder modal for mobile (replaces inline checkbox)
    if (this.preorderShowAgreement) {
      this.setupMobilePreorderModal();
    }

    // Select first component to sync state
    if (this.carouselCards.length > 0) {
      const firstLayerId = this.carouselCards[0].dataset.layerId;
      const isConfig = this.configLayers.some(l => l.layerId === firstLayerId);
      if (isConfig) this.selectComponent(firstLayerId);
    }
  }

  setupMobilePreorderModal() {
    // Auto-check the hidden checkbox so the normal validation passes
    const checkbox = document.querySelector('#cb');

    // Create modal overlay
    let overlay = document.querySelector('.preorder-confirm-overlay');
    if (overlay) return; // already exists

    overlay = document.createElement('div');
    overlay.className = 'preorder-confirm-overlay hidden';

    const modal = document.createElement('div');
    modal.className = 'preorder-confirm-modal';

    // Pull agreement text from the existing preorder-agreement element
    const agreementEl = document.querySelector('.preorder-agreement');
    let agreementText = '';
    let arrivalHtml = '';
    if (agreementEl) {
      // Get the text content excluding the checkbox
      const textDiv = agreementEl.querySelector('div');
      if (textDiv) {
        // Clone to extract arrival separately
        const clone = textDiv.cloneNode(true);
        const arrivalEl = clone.querySelector('.estimated-arrival');
        if (arrivalEl) {
          arrivalHtml = arrivalEl.outerHTML;
          arrivalEl.remove();
        }
        agreementText = clone.innerHTML;
      }
    }

    modal.innerHTML = `
      <p>${agreementText}</p>
      ${arrivalHtml}
      <div class="preorder-confirm-actions">
        <button type="button" class="preorder-confirm-cancel">Cancel</button>
        <button type="button" class="preorder-confirm-accept">I Agree</button>
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Event handlers
    modal.querySelector('.preorder-confirm-cancel').addEventListener('click', () => {
      overlay.classList.add('hidden');
      document.body.style.overflow = '';
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.classList.add('hidden');
        document.body.style.overflow = '';
      }
    });
    modal.querySelector('.preorder-confirm-accept').addEventListener('click', () => {
      overlay.classList.add('hidden');
      document.body.style.overflow = '';
      // Check the real checkbox and proceed with add-to-cart
      if (checkbox) checkbox.checked = true;
      this.updateAddToCartButton();
      // Trigger the actual add-to-cart
      const btn = document.querySelector('.add-to-cart-button');
      if (btn && !btn.disabled) btn.click();
    });

    // Intercept add-to-cart button to show modal instead
    const addBtn = document.querySelector('.add-to-cart-button');
    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        if (!this.isMobile || !this.preorderShowAgreement) return;
        if (addBtn.disabled) return;
        if (checkbox && checkbox.checked) return; // already agreed
        // Block the add-to-cart and show modal
        e.stopImmediatePropagation();
        e.preventDefault();
        overlay.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
      }, true); // capture phase to fire before the main handler
    }
  }

  buildMobileCarouselCard(layer, isConfigurable) {
    const card = document.createElement('div');
    card.className = 'mobile-component-card';
    card.dataset.layerId = layer.layerId;

    // Move the desktop component-card into this wrapper
    const desktopCard = document.querySelector(
      `.component-card[data-component-id="${layer.layerId}"]`
    );
    if (desktopCard) {
      desktopCard.style.display = 'block';
      card.appendChild(desktopCard);
    }

    if (isConfigurable) {
      // Add dropdown for configurable layers
      const optionsWrapper = document.createElement('div');
      optionsWrapper.className = 'mobile-card-options';
      this.populateMobileDropdown(optionsWrapper, layer);
      card.appendChild(optionsWrapper);
    } else {
      // Static/included card — show badge + note
      const infoDiv = document.createElement('div');
      infoDiv.className = 'mobile-included-info';
      infoDiv.innerHTML = `
        <div class="mobile-included-badge">Included</div>
        <p class="mobile-included-note">Included in every build</p>
        <p class="mobile-included-note">No configuration options</p>
      `;
      card.appendChild(infoDiv);
    }

    return card;
  }

  populateMobileDropdown(container, layer) {
    const position = this.optionPositionMap.get(layer.optionName);
    if (!position) return;

    const option = this.options.find(o => o.position === position);
    if (!option) return;

    const optContainer = document.createElement('div');
    optContainer.className = 'mobile-option-container';

    const label = document.createElement('label');
    label.textContent = option.name;
    optContainer.appendChild(label);

    const select = document.createElement('select');
    select.className = 'mobile-option-select';
    select.dataset.position = position;
    select.dataset.optionName = this.handleize(option.name);

    // Default option
    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = `Select ${option.name}`;
    defaultOpt.disabled = true;
    defaultOpt.selected = !this.selectedOptions.has(position);
    select.appendChild(defaultOpt);

    // Populate values
    const availableValues = this.getAvailableValuesForPosition(position);
    availableValues.forEach(({ value, labelText }) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = labelText;
      if (this.selectedOptions.get(position) === value) {
        opt.selected = true;
      }
      select.appendChild(opt);
    });

    select.addEventListener('change', (e) => this.handleOptionChange(e, layer));

    optContainer.appendChild(select);
    container.appendChild(optContainer);
  }

  refreshAllMobileDropdowns() {
    this.carouselCards.forEach(cardEl => {
      const layerId = cardEl.dataset.layerId;
      const layer = this.configLayers.find(l => l.layerId === layerId);
      if (!layer) return; // static layer, no dropdown

      const position = this.optionPositionMap.get(layer.optionName);
      if (!position) return;

      const select = cardEl.querySelector(`.mobile-option-select[data-position="${position}"]`);
      if (!select) return;

      const availableValues = this.getAvailableValuesForPosition(position);
      Array.from(select.options).forEach(opt => {
        if (!opt.value) return;
        const match = availableValues.find(v => v.value === opt.value);
        if (match) opt.textContent = match.labelText;
      });
    });
  }

  renderMobileCarousel() {
    const offsetX = 70;
    const scaleFactor = 0.8;

    this.carouselCards.forEach((cardEl, i) => {
      const diff = i - this.carouselIndex;
      const translateX = diff * offsetX;
      const scale = Math.max(1 - Math.abs(diff) * (1 - scaleFactor), 0.4);
      let opacity = 1 - Math.abs(diff) * 0.15;
      if (opacity < 0.3) opacity = 0.3;

      cardEl.style.transform = `translateX(${translateX}px) scale(${scale})`;
      cardEl.style.opacity = opacity;
      cardEl.style.zIndex = 100 - Math.abs(diff);

      if (diff === 0) {
        cardEl.classList.add('active');
      } else {
        cardEl.classList.remove('active');
      }
    });
  }

  attachGestureHandlers(container) {
    let isVerticalScroll = false;
    let isHorizontalScroll = false;
    let hasSwiped = false;

    container.addEventListener('touchstart', (e) => {
      this.carouselStartX = e.touches[0].clientX;
      this.carouselStartY = e.touches[0].clientY;
      hasSwiped = false;
      isVerticalScroll = false;
      isHorizontalScroll = false;
    });

    container.addEventListener('touchmove', (e) => {
      if (hasSwiped) return;

      const deltaX = e.touches[0].clientX - this.carouselStartX;
      const deltaY = e.touches[0].clientY - this.carouselStartY;

      if (!isHorizontalScroll && !isVerticalScroll) {
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
          isHorizontalScroll = true;
        } else if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
          isVerticalScroll = true;
        }
      }

      if (isHorizontalScroll) {
        e.preventDefault();

        if (Math.abs(deltaX) > 20) {
          hasSwiped = true;
          if (deltaX > 0) {
            this.goToCard(this.carouselIndex - 1);
          } else {
            this.goToCard(this.carouselIndex + 1);
          }
          this.carouselStartX = e.touches[0].clientX;
          this.carouselStartY = e.touches[0].clientY;
        }
      }
    }, { passive: false });
  }

  goToCard(newIndex) {
    const total = this.carouselCards.length;
    if (newIndex < 0) newIndex = total - 1;
    else if (newIndex >= total) newIndex = 0;
    this.carouselIndex = newIndex;
    this.renderMobileCarousel();

    // Select this component if it's configurable (updates preview, highlights)
    const layerId = this.carouselCards[newIndex].dataset.layerId;
    const isConfig = this.configLayers.some(l => l.layerId === layerId);
    if (isConfig) {
      this.selectComponent(layerId);
    }
  }

  handleResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const wasMobile = this.isMobile;
      const nowMobile = window.innerWidth <= 768;

      if (wasMobile === nowMobile) return;

      if (nowMobile) {
        this.initMobileLayout();
      } else {
        this.teardownMobileLayout();
      }
    }, 150);
  }

  teardownMobileLayout() {
    this.isMobile = false;

    // Move each desktop component-card back into .components-grid
    const grid = document.querySelector('.components-grid');
    if (grid) {
      this.carouselCards.forEach(wrapper => {
        const desktopCard = wrapper.querySelector('.component-card');
        if (desktopCard) {
          grid.appendChild(desktopCard);
        }
      });
    }

    // Move summary back to anchor and expand it
    const summary = document.querySelector('.configuration-summary');
    const anchor = document.querySelector('.configuration-summary-anchor');
    if (summary && anchor) {
      anchor.appendChild(summary);
      const header = summary.querySelector('.summary-header');
      const content = summary.querySelector('.summary-content');
      if (header && content) {
        header.setAttribute('aria-expanded', 'true');
        content.style.display = 'block';
      }
    }

    // Move title/price back to .configurator__options (prepend before options-container)
    const titleBlock = document.querySelector('.configurator-description');
    const optionsSection = document.querySelector('.configurator__options');
    if (titleBlock && optionsSection) {
      optionsSection.prepend(titleBlock);
    }

    // Move view toggle back into components header
    const viewToggle = document.querySelector('.view-toggle-container');
    const headerContainer = document.querySelector('.components-header-container');
    if (viewToggle && headerContainer) {
      // Unwrap from mobile-view-toggle wrapper
      const mobileWrap = viewToggle.closest('.mobile-view-toggle');
      if (mobileWrap) {
        headerContainer.appendChild(viewToggle);
        mobileWrap.remove();
      }
    }

    // Move components header back into .configurator__components
    const componentsSection = document.querySelector('.configurator__components');
    if (headerContainer && componentsSection) {
      componentsSection.prepend(headerContainer);
    }

    // Remove mobile elements
    const carousel = document.querySelector('.mobile-carousel-container');
    if (carousel) carousel.remove();
    const bottomBar = document.querySelector('.mobile-bottom-bar');
    if (bottomBar) bottomBar.remove();
    const titleWrapper = document.querySelector('.mobile-title-price');
    if (titleWrapper) titleWrapper.remove();
    const preorderOverlay = document.querySelector('.preorder-confirm-overlay');
    if (preorderOverlay) preorderOverlay.remove();

    this.carouselCards = [];

    // Uncheck preorder checkbox (was auto-checked by mobile modal)
    if (this.preorderShowAgreement) {
      const checkbox = document.querySelector('#cb');
      if (checkbox) checkbox.checked = false;
    }

    // Restore desktop state
    if (this.configurableIds.length > 0) {
      this.selectComponent(this.selectedComponent || this.configurableIds[0]);
    }
  }

  //#endregion
}

//#region Image Gallery Modal
document.addEventListener('DOMContentLoaded', function () {
  const modal = document.querySelector('.image-modal');
  if (!modal) return;

  const modalImage = modal.querySelector('.modal-image');
  const loadingOverlay = modal.querySelector('.modal-loading-overlay');
  const closeButton = modal.querySelector('.modal-close');
  const prevButton = modal.querySelector('.modal-prev');
  const nextButton = modal.querySelector('.modal-next');
  const thumbnails = document.querySelectorAll('.product-thumbnail');
  if (!thumbnails.length) return;

  let currentImageIndex = 0;
  const images = Array.from(thumbnails).map(thumb => ({
    fullRes: thumb.dataset.fullRes,
    thumbnail: thumb.dataset.thumbnail,
    alt: thumb.querySelector('img').alt
  }));

  function setLoading(loading) {
    loadingOverlay.classList.toggle('loading', loading);
    modalImage.classList.toggle('loading', loading);
  }

  // Create image counter element — place after .modal-image-container inside .modal-content
  const imageCounter = document.createElement('div');
  imageCounter.className = 'modal-image-counter';
  const modalContent = modal.querySelector('.modal-content');
  const modalImageContainer = modal.querySelector('.modal-image-container');
  modalContent.insertBefore(imageCounter, modalImageContainer.nextSibling);

  function updateCounter() {
    imageCounter.textContent = `${currentImageIndex + 1} / ${images.length}`;
  }

  function openModal(index) {
    currentImageIndex = index;
    updateModalImage();
    updateCounter();
    modal.classList.add('active');
  }

  function closeModal() {
    modal.classList.remove('active');
    setTimeout(() => {
      modalImage.src = images[currentImageIndex].thumbnail;
      setLoading(false);
    }, 300);
  }

  function updateModalImage() {
    // Show the 1200px thumbnail immediately — already cached from the grid
    modalImage.src = images[currentImageIndex].thumbnail;
    modalImage.alt = images[currentImageIndex].alt;

    const imageContainer = modal.querySelector('.modal-image-container');

    // Size the container based on the thumbnail (already loaded)
    const thumbImg = new Image();
    thumbImg.onload = function () {
      const viewportHeight = window.innerHeight * 0.85;
      const viewportWidth = window.innerWidth * 0.75;
      const imageRatio = thumbImg.naturalHeight / thumbImg.naturalWidth;

      let targetWidth, targetHeight;
      if (imageRatio * viewportWidth <= viewportHeight) {
        targetWidth = viewportWidth;
        targetHeight = viewportWidth * imageRatio;
      } else {
        targetHeight = viewportHeight;
        targetWidth = viewportHeight / imageRatio;
      }

      imageContainer.style.width = `${targetWidth}px`;
      imageContainer.style.height = `${targetHeight}px`;
    };
    thumbImg.src = images[currentImageIndex].thumbnail;

    // Show subtle loading indicator while high-res loads
    let indicator = modal.querySelector('.modal-loading-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'modal-loading-indicator';
      modal.querySelector('.modal-image-container').appendChild(indicator);
    }
    indicator.classList.add('visible');

    // Upgrade to higher-res version in background
    const highResImage = new Image();
    const targetIndex = currentImageIndex;
    highResImage.onload = function () {
      if (currentImageIndex === targetIndex) {
        modalImage.src = images[currentImageIndex].fullRes;
        indicator.classList.remove('visible');
      }
    };
    highResImage.onerror = function () {
      indicator.classList.remove('visible');
    };
    highResImage.src = images[currentImageIndex].fullRes;
  }

  function nextImage() {
    currentImageIndex = (currentImageIndex + 1) % images.length;
    updateModalImage();
    updateCounter();
  }

  function prevImage() {
    currentImageIndex = (currentImageIndex - 1 + images.length) % images.length;
    updateModalImage();
    updateCounter();
  }

  thumbnails.forEach((thumbnail, index) => {
    thumbnail.addEventListener('click', (e) => {
      e.preventDefault();
      openModal(index);
    });
  });

  closeButton.addEventListener('click', closeModal);
  nextButton.addEventListener('click', nextImage);
  prevButton.addEventListener('click', prevImage);

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  document.addEventListener('keydown', (e) => {
    if (!modal.classList.contains('active')) return;
    e.preventDefault();
    switch (e.key) {
      case 'Escape': closeModal(); break;
      case 'ArrowLeft': prevImage(); break;
      case 'ArrowRight': nextImage(); break;
    }
  });

  // Swipe gestures for mobile
  let swipeStartX = 0;
  let swipeStartY = 0;
  modal.addEventListener('touchstart', (e) => {
    swipeStartX = e.touches[0].clientX;
    swipeStartY = e.touches[0].clientY;
  }, { passive: true });

  modal.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - swipeStartX;
    const dy = e.changedTouches[0].clientY - swipeStartY;
    // Only trigger if horizontal swipe is dominant and long enough
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) nextImage();
      else prevImage();
    }
  }, { passive: true });
});
//#endregion
